import * as echarts from 'echarts';
import { pick } from 'lodash';
import isEqual from 'lodash/isEqual';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  convertFunnelDataToBarChartDataForFunnel,
  convertFunnelDataToBarChartDataForSankey,
} from '@src/emp-3.0/components/common/FunnelBarChart/convert';
import { IFunnel } from '@src/emp-3.0/types';
import { COLORS } from '@src/emp-3.0/utils/constants';

interface IFunnelBarChartProps {
  type: 'funnel' | 'sankey';
  field?: string;
  selectedNodes?: any[];
  funnelRes?: {
    event_names: string[];
    funnel_data: IFunnel[];
  };
  colors?: string[];
  style: React.CSSProperties;
}

const AXIS_LABEL_GAP = 20;

const FunnelBarChart: React.FC<IFunnelBarChartProps> = ({
  type = 'sankey',
  field = '',
  selectedNodes = [],
  funnelRes = { event_names: [], funnel_data: [] },
  colors = COLORS,
  style = {},
}) => {
  const chartDomRef = useRef<HTMLDivElement>(null);

  const [axisLabelWidth, setAxisLabelWidth] = useState<number>(200);

  const transformData = useMemo(() => {
    if (type === 'funnel') {
      return convertFunnelDataToBarChartDataForFunnel({
        field,
        funnelRes,
        axisLabelWidth,
        option: {
          color: colors,
        },
      });
    } else {
      return convertFunnelDataToBarChartDataForSankey(funnelRes, {
        color: selectedNodes.map((node) => node.color),
      });
    }
  }, [type, field, funnelRes, axisLabelWidth, selectedNodes, colors]);

  const resizeAxisLabelWidth = useCallback(() => {
    const boundingClientRect = chartDomRef.current?.getBoundingClientRect();
    if (boundingClientRect?.width) {
      const eventNameCount = funnelRes.event_names.length;
      const width = (boundingClientRect.width - AXIS_LABEL_GAP * eventNameCount) / eventNameCount;
      setAxisLabelWidth(width);
    }
  }, [funnelRes.event_names]);

  useEffect(() => {
    resizeAxisLabelWidth();

    window.addEventListener('resize', resizeAxisLabelWidth);
    return () => {
      window.removeEventListener('resize', resizeAxisLabelWidth);
    };
  }, [resizeAxisLabelWidth]);

  useEffect(() => {
    let chart;
    if (chartDomRef.current && transformData) {
      chart?.dispose();
      chart = echarts.init(chartDomRef.current, undefined, {
        height: type === 'sankey' ? 500 : 450, // Adjust height based on type
        renderer: 'svg',
      });
      chart.setOption(transformData);
    }
    const resizeChart = () => {
      chart?.resize();
    };
    window.addEventListener('resize', resizeChart);
    return () => {
      window.removeEventListener('resize', resizeChart);
      chart?.dispose();
    };
  }, [transformData, type]);

  return <div style={{ ...style }} ref={chartDomRef} />;
};

export default React.memo(FunnelBarChart, (prevProps, nextProps) => {
  return isEqual(
    pick(prevProps, ['type', 'field', 'selectedNodes', 'funnelRes']),
    pick(nextProps, ['type', 'field', 'selectedNodes', 'funnelRes']),
  );
});
